import { supabase } from '../lib/supabase';
import { subscribeToRestaurantChanges } from '../utils/realtimeSubscription';

const requireRestaurantId = (restaurantId) => {
  if (!restaurantId) {
    throw new Error('Restaurant context is required');
  }
};

// ─── Restaurant Profile ───────────────────────────────────────────────────────

export const fetchRestaurantProfile = async (restaurantId) => {
  requireRestaurantId(restaurantId);
  const { data, error } = await supabase?.from('restaurant_profile')?.select('*')?.eq('restaurant_id', restaurantId)?.limit(1)?.maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    id: data?.id,
    name: data?.name,
    tagline: data?.tagline,
    cuisine: data?.cuisine,
    phone: data?.phone,
    email: data?.email,
    address: data?.address,
    hours: data?.hours,
    currency: data?.currency,
    logo: data?.logo,
    logoAlt: data?.logo_alt,
    restaurantId: data?.restaurant_id,
  };
};

export const updateRestaurantProfile = async (profile, restaurantId) => {
  requireRestaurantId(restaurantId);

  const { error } = await supabase?.rpc('upsert_restaurant_profile', {
    p_restaurant_id: restaurantId,
    p_name:          profile?.name          ?? '',
    p_tagline:       profile?.tagline       ?? '',
    p_cuisine:       profile?.cuisine       ?? '',
    p_phone:         profile?.phone         ?? '',
    p_email:         profile?.email         ?? '',
    p_address:       profile?.address       ?? '',
    p_hours:         profile?.hours         ?? '',
    p_currency:      profile?.currency      ?? 'CAD',
    p_logo:          profile?.logo          ?? '',
    p_logo_alt:      profile?.logoAlt || profile?.logo_alt || '',
  });

  if (error) throw error;
};

// ─── Categories ───────────────────────────────────────────────────────────────

export const fetchCategories = async (restaurantId) => {
  requireRestaurantId(restaurantId);
  const { data, error } = await supabase?.from('menu_categories')?.select('*')?.eq('restaurant_id', restaurantId)?.order('sort_order', { ascending: true });
  if (error) throw error;
  return data?.map((cat) => ({
    id: cat?.id,
    name: cat?.name,
    sortOrder: cat?.sort_order,
    itemCount: 0,
  }));
};

export const addCategory = async (name, restaurantId) => {
  requireRestaurantId(restaurantId);
  const id = name?.toLowerCase()?.replace(/\s+/g, '-')?.replace(/[^a-z0-9-]/g, '') || `cat-${Date.now()}`;
  const { data: existing } = await supabase?.from('menu_categories')?.select('sort_order')?.eq('restaurant_id', restaurantId)?.order('sort_order', { ascending: false })?.limit(1)?.single();
  const sortOrder = (existing?.sort_order || 0) + 1;
  const payload = { id, name, sort_order: sortOrder };
  if (restaurantId) payload.restaurant_id = restaurantId;
  const { error } = await supabase?.from('menu_categories')?.insert(payload);
  if (error) throw error;
  return { id, name, sortOrder, itemCount: 0 };
};

export const updateCategory = async (id, name, restaurantId) => {
  requireRestaurantId(restaurantId);
  const { error } = await supabase?.from('menu_categories')?.update({ name })?.eq('id', id)?.eq('restaurant_id', restaurantId);
  if (error) throw error;
};

export const deleteCategory = async (id, restaurantId) => {
  requireRestaurantId(restaurantId);
  const { error } = await supabase?.from('menu_categories')?.delete()?.eq('id', id)?.eq('restaurant_id', restaurantId);
  if (error) throw error;
};

// ─── Dishes ───────────────────────────────────────────────────────────────────

export const fetchDishes = async (restaurantId) => {
  requireRestaurantId(restaurantId);
  const { data, error } = await supabase?.from('menu_dishes')?.select('*')?.eq('restaurant_id', restaurantId)?.order('sort_order', { ascending: true });
  if (error) throw error;
  return data?.map(dbDishToApp);
};

export const upsertDish = async (dish, restaurantId) => {
  requireRestaurantId(restaurantId);
  const payload = appDishToDb(dish);
  payload.restaurant_id = restaurantId;
  const { error } = await supabase?.from('menu_dishes')?.upsert(payload, { onConflict: 'restaurant_id,id' });
  if (error) throw error;
};

export const upsertDishes = async (dishes, restaurantId) => {
  requireRestaurantId(restaurantId);
  const payloads = dishes?.map(d => {
    const p = appDishToDb(d);
    p.restaurant_id = restaurantId;
    return p;
  });
  const { error } = await supabase?.from('menu_dishes')?.upsert(payloads, { onConflict: 'restaurant_id,id' });
  if (error) throw error;
};

export const insertDish = async (dish, restaurantId) => {
  requireRestaurantId(restaurantId);
  // Generate a numeric id based on timestamp
  const newId = Date.now();
  const payload = appDishToDb({ ...dish, id: newId });
  payload.restaurant_id = restaurantId;
  const { error } = await supabase?.from('menu_dishes')?.insert(payload);
  if (error) throw error;
  return { ...dish, id: newId };
};

export const deleteDish = async (id, restaurantId) => {
  requireRestaurantId(restaurantId);
  const { error } = await supabase?.from('menu_dishes')?.delete()?.eq('id', id)?.eq('restaurant_id', restaurantId);
  if (error) throw error;
};

// ─── Real-time subscriptions ──────────────────────────────────────────────────

export const subscribeToMenuDishes = (callback, restaurantId) => {
  return subscribeToRestaurantChanges({
    channelName: 'menu_dishes_changes',
    table: 'menu_dishes',
    restaurantId,
    callback,
  });
};

export const subscribeToCategories = (callback, restaurantId) => {
  return subscribeToRestaurantChanges({
    channelName: 'menu_categories_changes',
    table: 'menu_categories',
    restaurantId,
    callback,
  });
};

export const subscribeToRestaurantProfile = (callback, restaurantId) => {
  return subscribeToRestaurantChanges({
    channelName: 'restaurant_profile_changes',
    table: 'restaurant_profile',
    restaurantId,
    callback,
  });
};

export const fetchPublicMenu = async (slug) => {
  const { data, error } = await supabase?.rpc('get_public_menu', { p_slug: slug });
  if (error) throw error;
  if (!data) return null;
  return data;
};

// ─── Converters ───────────────────────────────────────────────────────────────

function dbDishToApp(d) {
  return {
    id: d?.id,
    name: d?.name,
    description: d?.description || '',
    price: parseFloat(d?.price) || 0,
    originalPrice: d?.original_price ? parseFloat(d?.original_price) : null,
    category: d?.category,
    image: d?.image || '',
    imageAlt: d?.image_alt || '',
    ingredients: d?.ingredients || [],
    calories: d?.calories || 0,
    isNew: d?.is_new || false,
    isPopular: d?.is_popular || false,
    isVegetarian: d?.is_vegetarian || false,
    spiceLevel: d?.spice_level || 0,
    arUrl: d?.ar_url || '',
    hidden: d?.hidden || false,
    sortOrder: d?.sort_order || 0,
    widthCm: d?.width_cm ? parseFloat(d?.width_cm) : null,
    heightCm: d?.height_cm ? parseFloat(d?.height_cm) : null,
    depthCm: d?.depth_cm ? parseFloat(d?.depth_cm) : null,
    chips: Array.isArray(d?.chips) ? d?.chips : (d?.chip ? [d?.chip] : []),
    restaurantId: d?.restaurant_id,
  };
}

function appDishToDb(d) {
  return {
    id: d?.id,
    name: d?.name,
    description: d?.description || '',
    price: parseFloat(d?.price) || 0,
    original_price: d?.originalPrice ? parseFloat(d?.originalPrice) : null,
    category: d?.category || 'mains',
    image: d?.image || '',
    image_alt: d?.imageAlt || d?.image_alt || '',
    ingredients: d?.ingredients || [],
    calories: d?.calories || 0,
    is_new: d?.isNew || d?.is_new || false,
    is_popular: d?.isPopular || d?.is_popular || false,
    is_vegetarian: d?.isVegetarian || d?.is_vegetarian || false,
    spice_level: d?.spiceLevel || d?.spice_level || 0,
    ar_url: d?.arUrl || d?.ar_url || '',
    hidden: d?.hidden || false,
    sort_order: d?.sortOrder || d?.sort_order || 0,
    width_cm: d?.widthCm != null ? parseFloat(d?.widthCm) : (d?.width_cm != null ? parseFloat(d?.width_cm) : null),
    height_cm: d?.heightCm != null ? parseFloat(d?.heightCm) : (d?.height_cm != null ? parseFloat(d?.height_cm) : null),
    depth_cm: d?.depthCm != null ? parseFloat(d?.depthCm) : (d?.depth_cm != null ? parseFloat(d?.depth_cm) : null),
    chips: Array.isArray(d?.chips) ? d?.chips : [],
    updated_at: new Date()?.toISOString(),
  };
}
